/**
 * 舊資料的一級數字欄 → 起手的 `quotas`（批114）。
 *
 * ## 為什麼需要這支
 *
 * 批114 把所有核發類表單的獨立數字欄（席位、上限那幾個）全部併進 `quotas`，表單只送
 * 一個完整的配額包。但**資料庫裡的舊資料還是一級欄位**：
 * - 批114 之前建的草稿（`license_requests.seats_requested`、`request_json.requested.*`）
 * - 批114 之前簽的證（`licenses.seats`、`licenses.max_product_lines` 這幾個相容輸出欄）
 *
 * 打開舊草稿／以舊證為底換發時，如果只讀 `quotas`，那些數字會**憑空消失**（表單退回
 * `absent_default`，多半是 0 或不限，一送出就把客戶的上限改掉）。這支就是那道銜接。
 *
 * ## 判準是**鍵存在性**，不是 `!= null`
 *
 * 與 `components/ReissueModal.vue`（`watch(plan)` 那段）逐字相同的理由：`quotas` 裡
 * 「鍵缺席」與「鍵存在但值是 null」是**兩種不同的語意**——前者交給 `absent_default`、
 * 後者是明確的「不限」。用 `!= null` 判會把客戶明確選的「不限」蓋回舊數字。
 *
 * ## ★★ 全前端唯一允許出現舊一級欄位名的地方
 *
 * LS 前端的規則是「零產品字面」（看門狗 `lib/productVocabulary.spec.ts`）——鍵名、標籤、
 * 必填與否全部由憑證設計驅動。這支是唯一的例外：它的工作就是**銜接後端相容輸出的鍵名**，
 * 那些鍵名是既定的對外契約（見 `license-server/docs/PORTAL_LINK_API.md`、
 * `SERVICE_API.md`，後端集中在 `services/quota_compat.py`）。所以字面只准出現在
 * 下面這一個常數物件裡，看門狗對本檔明確豁免。
 */
import type { ProductSpec } from '@/api/ls'

/**
 * 舊資料的一級數字欄名 → 對應的配額鍵。
 *
 * - 鍵＝物件上的欄位名（後端相容輸出／舊草稿 JSON 的一級鍵）。
 * - 值＝憑證設計裡的配額鍵名。兩者目前逐字相同，分成兩欄是為了「哪天鍵名不同」時
 *   只改這裡一行，而不是全前端 grep。
 *
 * ★ 只補「憑證設計有宣告」的鍵：第三方產品沒有這兩個鍵時，這支等於不做事。
 */
export const LEGACY_COUNT_FIELDS: Record<string, string> = {
  seats: 'seats',
  max_product_lines: 'max_product_lines',
}

/**
 * 把舊資料的一級數字欄併進 `quotas`，回傳表單可以直接起手的完整包。
 *
 * @param spec 該產品的憑證設計；null＝還沒載回來 → 一個鍵都不補（不知道有沒有宣告）。
 * @param src  任何帶 `quotas` 與舊一級欄位的物件（授權、草稿列、`request_json.requested`…）。
 *             刻意吃 `unknown`：呼叫端的型別五花八門（多半是 interface，帶索引簽章的
 *             參數型別會讓它們全部編不過），這裡一律以安全轉型讀。
 * @returns 併好的 quotas；`quotas` 缺席／為 null **且**沒有任何可補的值時回 `null`
 *          （＝「不指定」，語意與原本一樣交給後端從套裝模板展開）。
 */
export function seedQuotas(
  spec: ProductSpec | null | undefined,
  src: unknown,
): Record<string, number | null> | null {
  const o = (src && typeof src === 'object' ? src : {}) as Record<string, unknown>
  const raw = o.quotas
  const base =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? { ...(raw as Record<string, number | null>) }
      : null
  const declared = new Set((spec?.quotas ?? []).map((q) => q.key))
  const merged: Record<string, number | null> = { ...(base ?? {}) }
  let added = false
  for (const [field, key] of Object.entries(LEGACY_COUNT_FIELDS)) {
    if (!declared.has(key)) continue
    // ★ 鍵存在性判準：`quotas` 已經有這個鍵（哪怕值是 null＝明確不限）就不碰。
    if (key in merged) continue
    const v = o[field]
    // 只補**數字**：一級欄位的 null 多半是「這筆資料沒有這個概念」而不是「明確不限」，
    // 交給 `absent_default` 比硬寫 null 安全（明確的不限一定會以鍵的形式存在 quotas 裡）。
    if (typeof v !== 'number') continue
    merged[key] = v
    added = true
  }
  if (base === null && !added) return null
  return merged
}
